import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { RingHealth, ServiceHealth } from '../api'
import { PageDescription } from '../components/PageDescription'

const STATUS_STYLES: Record<string, { dot: string; badge: string; label: string }> = {
    healthy:  { dot: 'bg-green-400',  badge: 'bg-green-500/15 text-green-400 ring-1 ring-green-500/30',  label: 'healthy' },
    degraded: { dot: 'bg-amber-400',  badge: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30',  label: 'degraded' },
    error:    { dot: 'bg-red-400',    badge: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30',         label: 'error' },
    unknown:  { dot: 'bg-slate-500',  badge: 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/30',   label: 'unknown' },
}

function fmt(n: number | null | undefined, decimals = 1): string {
    return n == null ? '—' : n.toFixed(decimals)
}

function StatusBadge({ status }: { status: string }) {
    const s = STATUS_STYLES[status] ?? STATUS_STYLES['unknown']!
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${s.badge}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
            {s.label}
        </span>
    )
}

function ServiceRow({ svc }: { svc: ServiceHealth }) {
    const s = STATUS_STYLES[svc.status] ?? STATUS_STYLES['unknown']!
    return (
        <div className="flex items-center gap-3 py-2.5 border-b border-slate-700/50 last:border-0">
            <span className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${s.dot}`} />
            <span className="text-sm font-medium text-slate-200 w-28 shrink-0 font-mono">{svc.name}</span>
            <div className="flex items-center gap-4 text-xs text-slate-400 ml-auto tabular-nums">
                <span title="Requests per minute">
                    <span className="text-slate-500 mr-1">req/min</span>
                    <span className="text-slate-300">{fmt(svc.requestsPerMin, 0)}</span>
                </span>
                <span title="Error rate">
                    <span className="text-slate-500 mr-1">err%</span>
                    <span className={svc.errorPct != null && svc.errorPct > 1 ? 'text-amber-400' : 'text-slate-300'}>
                        {fmt(svc.errorPct, 2)}%
                    </span>
                </span>
                <span title="p95 response time">
                    <span className="text-slate-500 mr-1">p95</span>
                    <span className={svc.p95Ms != null && svc.p95Ms > 500 ? 'text-amber-400' : 'text-slate-300'}>
                        {fmt(svc.p95Ms, 0)}ms
                    </span>
                </span>
            </div>
        </div>
    )
}

function RingCard({ ring }: { ring: RingHealth }) {
    const overallStatus = ring.services.some(s => s.status === 'error')
        ? 'error'
        : ring.services.some(s => s.status === 'degraded')
        ? 'degraded'
        : ring.services.every(s => s.status === 'healthy')
        ? 'healthy'
        : 'unknown'

    return (
        <div className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 bg-slate-800/60">
                <span className="text-base font-semibold text-white">{ring.displayName}</span>
                <StatusBadge status={overallStatus} />
            </div>
            <div className="px-4">
                {ring.services.map(svc => (
                    <ServiceRow key={svc.name} svc={svc} />
                ))}
            </div>
            <div className="px-4 py-2 text-[10px] text-slate-600">
                as of {new Date(ring.asOf).toLocaleTimeString()}
            </div>
        </div>
    )
}

export function HealthPage() {
    const { data, isLoading, isError, error, dataUpdatedAt, refetch, isFetching } = useQuery({
        queryKey: ['health'],
        queryFn: releaseApi.getRingHealth,
        refetchInterval: 60_000,
    })

    return (
        <>
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">Landscape Health</span>
                        <PageDescription subtitle="Live metrics from Application Insights" storageKey="desc-health" detail="Aggregated health metrics per ring, sourced from Azure Application Insights. Shows request rates, failure rates, and response times for each service in each ring. A red ring means one or more services are above their failure threshold." />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                        {dataUpdatedAt > 0 && (
                            <span>Updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>
                        )}
                        <button
                            onClick={() => void refetch()}
                            disabled={isFetching}
                            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors disabled:opacity-40"
                        >
                            {isFetching ? 'Refreshing…' : 'Refresh'}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 w-full">
                {isLoading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-48 rounded-xl border border-slate-700 bg-slate-800/40 animate-pulse" />
                        ))}
                    </div>
                )}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm">
                        Failed to load health data:{' '}
                        {error instanceof Error ? error.message : 'Unknown error'}
                    </div>
                )}

                {data && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.rings.map(ring => (
                            <RingCard key={ring.ring} ring={ring} />
                        ))}
                    </div>
                )}
            </main>
        </>
    )
}
